import React, { useState } from "react";
import { AlertTriangle, AlertCircle, Info, Check } from "lucide-react";
import { timeAgo, api } from "@/lib/api";

const icons = {
  critical: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const colors = {
  critical: { dot: "var(--status-red)", bg: "var(--status-red-bg)" },
  warning:  { dot: "var(--status-amber)", bg: "var(--status-amber-bg)" },
  info:     { dot: "var(--status-blue)", bg: "var(--status-blue-bg)" },
};

export default function AlertItem({ alert, onAcknowledged, onClick }) {
  const sev = (alert.severity || "info").toLowerCase();
  const Icon = icons[sev] || Info;
  const c = colors[sev] || colors.info;
  const [ackState, setAckState] = useState(alert.acknowledged ? "acked" : "idle");

  const acknowledge = async (e) => {
    e.stopPropagation();
    if (ackState !== "idle") return;
    setAckState("acking");
    try {
      await api.post(`/alerts/${alert.id}/acknowledge`);
      setAckState("acked");
      setTimeout(() => {
        if (onAcknowledged) onAcknowledged(alert.id);
      }, 1500);
    } catch (err) {
      setAckState("idle");
    }
  };

  return (
    <div
      className="flex items-start gap-3 px-3 py-3 rounded-xl hover:bg-white/40 transition-all"
      style={{
        opacity: ackState === "acked" ? 0 : 1,
        transform: ackState === "acked" ? "translateX(20px)" : "translateX(0)",
        transition: "opacity 1.2s ease, transform 1.2s ease",
      }}
      onClick={onClick}
      data-testid={`alert-${alert.machine_id}`}
    >
      <div className="flex-shrink-0 mt-0.5">
        <div className="relative">
          <div className="w-2.5 h-2.5 rounded-full pulse-dot" style={{ background: c.dot }} />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono text-[12px] font-medium" style={{ color: "var(--slate-900)" }}>
            {alert.machine_id}
          </span>
          <span
            className={`status-pill ${sev === "critical" ? "red" : sev === "warning" ? "amber" : "blue"}`}
            style={{ fontSize: "10px", padding: "1px 7px" }}
          >
            {sev}
          </span>
        </div>
        <p className="text-[12px] leading-snug" style={{ color: "var(--slate-700)" }}>
          {alert.description}
        </p>
        <div className="text-[11px] mt-1" style={{ color: "var(--slate-500)" }}>
          {timeAgo(alert.created_at)}
        </div>
      </div>
      <div className="flex-shrink-0 self-center">
        {ackState === "acked" ? (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-medium"
            style={{ color: "var(--status-green)" }}
            data-testid={`alert-ack-done-${alert.machine_id}`}
          >
            <Check size={12} /> Acknowledged
          </span>
        ) : (
          <button
            className="alert-ack-btn"
            onClick={acknowledge}
            disabled={ackState === "acking"}
            data-testid={`alert-ack-${alert.machine_id}`}
          >
            {ackState === "acking" ? "…" : "Acknowledge"}
          </button>
        )}
      </div>
    </div>
  );
}
